/**
 * StaffMessagesThread — M2.2.4 · WhatsApp-style bubbles + font leggibile
 *  + AddToCRM header button + AudioStatusBar + wider desktop layout.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileText, Mic, AlertTriangle, Check, CheckCheck, X, Download, ImageIcon, UserPlus, WifiOff, Bug, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { getMediaBlobUrl } from "@/lib/mediaBlobCache";
import { AudioStatusBar } from "@/components/AudioStatusBar";
import { AddCustomerModal } from "@/components/AddCustomerModal";
import { EmojiPicker } from "@/components/EmojiPicker";
import { TemplatePickerModal } from "@/components/staff/TemplatePickerModal";
import { Send, Smile } from "lucide-react";
import { lifecycle } from "@/lib/lifecycle";
import { useConnectivity } from "@/lib/connectivity";
import { useAuth } from "@/lib/auth";

const POLL_MS = 5000;

// Chiave sessionStorage per persistere la draft del composer per conversazione.
// Sopravvive a refresh/reload/redirect al login → hydrata al re-mount.
const DRAFT_KEY = (id) => `sb.draft.thread.${id}`;

function formatWaId(waId) {
  if (!waId) return "";
  const s = String(waId).replace(/^\+/, "");
  if (s.length < 7) return "+" + s;
  const cc = s.slice(0, 2);
  const rest = s.slice(cc.length);
  const groups = [];
  let i = 0;
  while (i < rest.length) {
    const take = (rest.length - i <= 4) ? rest.length - i : 3;
    groups.push(rest.slice(i, i + take));
    i += take;
  }
  return `+${cc} ${groups.join(" ")}`;
}

function bubbleTime(iso, ts) {
  if (iso) return new Date(iso).toLocaleTimeString("it-IT",
    { hour: "2-digit", minute: "2-digit" });
  if (ts) return new Date(ts * 1000).toLocaleTimeString("it-IT",
    { hour: "2-digit", minute: "2-digit" });
  return "";
}

// -----------------------------------------------------------------------
// M2.5 P0-C · Date separators (Europe/Rome). WABA-style.
// dayKey: chiave YYYY-MM-DD del giorno LOCALE Europe/Rome (rispetto al
// timestamp Meta reale). Coerente indipendentemente dall'orario UTC.
// -----------------------------------------------------------------------
const _ROME_DAY_FMT = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Rome",
  year: "numeric", month: "2-digit", day: "2-digit",
});
function dayKeyRome(d) {
  // Ritorna "YYYY-MM-DD" del giorno Europe/Rome relativo al Date istante.
  const parts = _ROME_DAY_FMT.formatToParts(d);
  const y = parts.find((p) => p.type === "year").value;
  const m = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  return `${y}-${m}-${day}`;
}
function messageInstant(m) {
  if (m?.received_at) return new Date(m.received_at);
  if (m?.meta_timestamp) return new Date(m.meta_timestamp * 1000);
  if (m?.created_at)  return new Date(m.created_at);
  return null;
}
function messageDayKey(m) {
  const d = messageInstant(m);
  return d ? dayKeyRome(d) : null;
}
const _ROME_LONG_FMT = new Intl.DateTimeFormat("it-IT", {
  timeZone: "Europe/Rome", day: "numeric", month: "long",
});
const _ROME_LONG_FMT_WITH_YEAR = new Intl.DateTimeFormat("it-IT", {
  timeZone: "Europe/Rome", day: "numeric", month: "long", year: "numeric",
});
function daySeparatorLabel(dayKey, nowDate = new Date()) {
  if (!dayKey) return "";
  const todayKey = dayKeyRome(nowDate);
  const yestDate = new Date(nowDate.getTime() - 24 * 60 * 60 * 1000);
  const yesterdayKey = dayKeyRome(yestDate);
  if (dayKey === todayKey) return "Oggi";
  if (dayKey === yesterdayKey) return "Ieri";
  // Costruisci un Date "mezzogiorno UTC" del giorno per formattarlo con
  // il DateTimeFormat locale Europe/Rome — sicuro contro DST edge cases.
  const [y, m, d] = dayKey.split("-").map(Number);
  const at12Utc = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const todayYear = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Rome", year: "numeric",
  }).format(nowDate);
  return String(y) === todayYear
    ? _ROME_LONG_FMT.format(at12Utc)
    : _ROME_LONG_FMT_WITH_YEAR.format(at12Utc);
}

function DaySeparator({ label }) {
  return (
    <div className="flex justify-center my-2" data-testid="day-separator"
          data-day-label={label}>
      <span className="px-3 py-1 rounded-full bg-white/85 border border-sand-200
                        text-[11px] font-medium text-ink-600 shadow-sm">
        {label}
      </span>
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "read") return <CheckCheck className="w-3.5 h-3.5 text-blue-500" />;
  if (status === "delivered") return <CheckCheck className="w-3.5 h-3.5 text-ink-400" />;
  if (status === "sent") return <Check className="w-3.5 h-3.5 text-ink-400" />;
  if (status === "failed") return <AlertTriangle className="w-3.5 h-3.5 text-red-500" />;
  return null;
}

function useMediaBlob(messageId) {
  const [state, setState] = useState({ url: null, err: null, loading: true });
  useEffect(() => {
    if (!messageId) return;
    let cancelled = false;
    getMediaBlobUrl(messageId)
      .then(({ url }) => { if (!cancelled) setState({ url, err: null, loading: false }); })
      .catch((e) => { if (!cancelled) setState({ url: null, err: formatApiError(e), loading: false }); });
    return () => { cancelled = true; };
  }, [messageId]);
  return state;
}

function ImageSkeleton() {
  return (
    <div className="w-[240px] h-[170px] rounded-xl bg-sand-200 overflow-hidden relative"
         data-testid="media-skeleton">
      <div className="absolute inset-0 flex items-center justify-center">
        <ImageIcon className="w-8 h-8 text-sand-400" strokeWidth={1.5} />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent
                      animate-[pulse_1.4s_ease-in-out_infinite]" />
    </div>
  );
}

function ImageBubble({ m, onOpenLightbox }) {
  const { url, err, loading } = useMediaBlob(m.id);
  if (err) return (
    <div className="flex items-center gap-2 text-xs text-red-600 italic max-w-[240px]">
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <span className="truncate">Impossibile caricare immagine</span>
    </div>
  );
  if (loading || !url) return <ImageSkeleton />;
  return (
    <img src={url} alt="Immagine WhatsApp"
         data-testid={`msg-image-${m.id}`}
         onClick={() => onOpenLightbox(url)}
         className="max-w-full rounded-xl cursor-zoom-in max-h-[320px] object-cover" />
  );
}

function AudioBubble({ m }) {
  const { url, err, loading } = useMediaBlob(m.id);
  if (err) return (
    <div className="flex items-center gap-2 text-ink-500 text-sm">
      <Mic className="w-4 h-4" />
      <span className="italic text-red-600 text-xs">Audio non disponibile</span>
    </div>
  );
  return (
    <div className="flex items-center gap-2">
      <Mic className="w-4 h-4 text-ink-500 flex-shrink-0" />
      {url ? (
        <audio controls src={url} data-testid={`msg-audio-${m.id}`}
               className="max-w-[240px] h-8" />
      ) : loading ? (
        <div className="h-8 w-[200px] rounded-full bg-sand-200
                        animate-[pulse_1.4s_ease-in-out_infinite]"
             data-testid="media-skeleton" />
      ) : null}
    </div>
  );
}

function DocumentBubble({ m }) {
  const [downloading, setDownloading] = useState(false);
  const filename = m.media?.filename || "documento";
  const doDownload = async () => {
    setDownloading(true);
    try {
      const { url } = await getMediaBlobUrl(m.id);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
    } catch { /* silent */ }
    finally { setDownloading(false); }
  };
  return (
    <div className="flex items-center gap-2 text-ink-700 text-sm max-w-[300px]">
      <FileText className="w-4 h-4 flex-shrink-0" />
      <span className="truncate flex-1">{filename}</span>
      <button onClick={doDownload}
              data-testid={`msg-doc-download-${m.id}`}
              disabled={downloading}
              className="p-1.5 rounded-full hover:bg-black/10 disabled:opacity-50">
        <Download className="w-4 h-4" />
      </button>
    </div>
  );
}

function ReactionsRow({ reactions, isOut }) {
  if (!reactions || reactions.length === 0) return null;
  const byEmoji = {};
  for (const r of reactions) {
    if (!r.emoji) continue;
    byEmoji[r.emoji] = (byEmoji[r.emoji] || 0) + 1;
  }
  const entries = Object.entries(byEmoji);
  if (entries.length === 0) return null;
  return (
    <div className={`flex gap-1 -mt-1 ${isOut ? "justify-end" : "justify-start"}`}
         data-testid="msg-reactions-row">
      {entries.map(([emo, n]) => (
        <span key={emo}
              data-testid={`reaction-${emo}`}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full
                          bg-white border border-sand-300 text-xs shadow-sm">
          <span>{emo}</span>
          {n > 1 && <span className="text-ink-500">{n}</span>}
        </span>
      ))}
    </div>
  );
}

function MessageBubble({ m, onOpenLightbox }) {
  const isOut = m.direction === "out";
  const isUnsupported = m.type === "unsupported";
  // M2.2.4 · Bolle stile WhatsApp Business:
  //   inbound  → bianca, sinistra, angolo TL squadrato
  //   outbound → verde chiaro (#DCF8C6 style, tema Sarabi bronze-neutro), destra, angolo TR squadrato
  const bubbleCls = isOut
    ? "bg-[#DCF8C6] border border-[#B9E5A0] rounded-tr-md"
    : "bg-white border border-sand-300 rounded-tl-md";
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"} px-3`}
         data-testid={`msg-${m.id}`}
         data-direction={m.direction}
         data-type={m.type}>
      <div className="flex flex-col max-w-[75%] min-w-[80px]">
        <div className={`rounded-2xl px-3.5 py-2 shadow-sm ${bubbleCls}`}>
          {m.type === "text" && (
            <div className="whitespace-pre-wrap break-words text-[15px] leading-[1.35] text-ink-900">
              {m.text?.body || ""}
            </div>
          )}
          {m.type === "template" && (
            <div data-testid={`msg-template-${m.id}`}>
              <div className="whitespace-pre-wrap break-words text-[15px] leading-[1.35] text-ink-900">
                {m.text?.body || ""}
              </div>
              <div className="mt-1 inline-flex items-center gap-1 text-[10.5px]
                              text-ink-500/80 italic">
                <FileText className="w-3 h-3" />
                <span>Template</span>
              </div>
            </div>
          )}
          {m.type === "button" && (
            <div data-testid={`msg-button-${m.id}`}>
              <div className="whitespace-pre-wrap break-words text-[15px] leading-[1.35] text-ink-900">
                {m.text?.body || m.button_meta?.text || m.button_meta?.payload || ""}
              </div>
              <div className="mt-1 text-[10.5px] text-ink-500/80 italic">
                ↩ Risposta rapida
              </div>
            </div>
          )}
          {m.type === "image" && <ImageBubble m={m} onOpenLightbox={onOpenLightbox} />}
          {m.type === "audio" && <AudioBubble m={m} />}
          {m.type === "document" && <DocumentBubble m={m} />}
          {(m.type === "video" || m.type === "sticker") && (
            <div className="text-sm text-ink-500 italic">
              [{m.type}] {m.media?.mime_type || ""}
            </div>
          )}
          {isUnsupported && (
            <div className="flex items-start gap-2 text-ink-500 text-sm">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="italic">
                  Questo tipo di messaggio non può essere visualizzato tramite le API WhatsApp Business utilizzate da Sarabi OS.
                </div>
                {(m.unsupported?.type || m.unsupported?.raw_type) && (
                  <div className="text-xs text-ink-400 mt-1"
                        data-testid={`unsupported-type-${m._id || m.id_meta}`}>
                    Tipo: {m.unsupported?.type || m.unsupported?.raw_type}
                  </div>
                )}
              </div>
            </div>
          )}
          {m.context?.kind === "forward" && (
            <div className="text-xs text-ink-400 italic mt-1">↪ inoltrato</div>
          )}
          <div className={`flex items-center gap-1 mt-0.5 text-[10.5px]
                            ${isOut ? "text-ink-500/80 justify-end" : "text-ink-400 justify-end"}`}>
            <span>{bubbleTime(m.received_at, m.meta_timestamp)}</span>
            {isOut && <StatusIcon status={m.last_status} />}
            {m.is_history && <span className="italic">· storico</span>}
          </div>
        </div>
        <ReactionsRow reactions={m.reactions} isOut={isOut} />
      </div>
    </div>
  );
}


export default function StaffMessagesThread() {
  const { conversationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [convMeta, setConvMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const timer = useRef(null);
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const markedRead = useRef(false);
  const endRef = useRef(null);
  const lastTsRef = useRef(0);

  // M2.3 · Composer state (send/reply WhatsApp text)
  // Draft persistita in sessionStorage (recovery cross-device).
  const [draft, setDraft] = useState(() => {
    try { return sessionStorage.getItem(DRAFT_KEY(conversationId)) || ""; }
    catch { return ""; }
  });
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const clientReqIdRef = useRef(null);
  const textareaRef = useRef(null);
  const { online } = useConnectivity();

  // P0-5 · Customer Service Window (24h) — server-side, letto dall'API
  // conversation. `open=false` disabilita il composer e mostra la CTA
  // "Invia template WhatsApp". Il calcolo autoritativo è backend; il FE
  // aggiorna localmente sui polling + sul fallback 409 whatsapp_window_closed.
  const [windowState, setWindowState] = useState({
    open: true,   // ottimistico finché la prima load non risponde
    expires_at: null,
    last_inbound_at: null,
  });
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  const mergeMessages = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    setMessages((prev) => {
      const byId = new Map(prev.map((m) => [m.id, m]));
      for (const m of incoming) byId.set(m.id, m);
      const merged = Array.from(byId.values());
      merged.sort((a, b) => (a.meta_timestamp || 0) - (b.meta_timestamp || 0));
      const top = merged[merged.length - 1];
      if (top && top.meta_timestamp) lastTsRef.current = top.meta_timestamp;
      return merged;
    });
  }, []);

  const loadInitial = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const { data } = await api.get(
        `/staff/messaging/whatsapp/conversations/${conversationId}/messages`,
        { params: { limit: 100, _t: Date.now() },
          headers: { "Cache-Control": "no-cache" }});
      if (!mounted.current) return;
      setConvMeta({
        wa_id: data.wa_id, wa_id_e164: data.wa_id_e164,
        customer_id: data.customer_id, customer_name: data.customer_name,
        customer_match_status: data.customer_match_status,
        display_name: data.display_name, display_name_source: data.display_name_source,
      });
      // P0-5 · sync window state dal payload server-side
      setWindowState({
        open: !!data.customer_service_window_open,
        expires_at: data.customer_service_window_expires_at || null,
        last_inbound_at: data.last_customer_inbound_at || null,
      });
      const items = data.items || [];
      setMessages(items);
      if (items.length) lastTsRef.current = items[items.length - 1].meta_timestamp || 0;
      setError(null);
    } catch (e) {
      if (mounted.current) setError(formatApiError(e));
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, [conversationId]);

  const pollNew = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      // M2.3.1 · Fix live status: rimuoviamo `after_ts` (che filtrava solo
      // messaggi nuovi) e rifetchiamo l'intera coda recente (limit=100).
      // `mergeMessages` fa dedup per id e SOVRASCRIVE i doc esistenti →
      // le transizioni sent → delivered → read propagano automaticamente
      // sulla stessa bolla senza F5 e senza duplicati.
      const { data } = await api.get(
        `/staff/messaging/whatsapp/conversations/${conversationId}/messages`,
        { params: { limit: 100, _t: Date.now() },
          headers: { "Cache-Control": "no-cache" }});
      if (!mounted.current) return;
      const incoming = data.items || [];
      mergeMessages(incoming);
      // P0-5 · sync window state ad ogni poll (auto-refresh)
      setWindowState({
        open: !!data.customer_service_window_open,
        expires_at: data.customer_service_window_expires_at || null,
        last_inbound_at: data.last_customer_inbound_at || null,
      });
      setError(null);
    } catch (e) {
      if (mounted.current) setError(formatApiError(e));
    } finally { inFlight.current = false; }
  }, [conversationId, mergeMessages]);

  const schedule = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (!mounted.current || document.hidden) return;
    timer.current = setTimeout(async () => { await pollNew(); schedule(); }, POLL_MS);
  }, [pollNew]);

  useEffect(() => {
    mounted.current = true;
    loadInitial().then(() => schedule());
    (async () => {
      if (markedRead.current) return;
      markedRead.current = true;
      try {
        await api.post(`/staff/messaging/whatsapp/conversations/${conversationId}/mark-read`, {});
        window.dispatchEvent(new CustomEvent("sb-messages-changed"));
      } catch { /* silent */ }
    })();
    // Cross-device recovery unificato via lifecycle.
    const offRecovery = lifecycle.on("recovery", () => {
      if (mounted.current) { pollNew(); schedule(); }
    });
    const offHidden = lifecycle.on("hidden", () => {
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    });
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
      offRecovery();
      offHidden();
    };
  }, [conversationId, loadInitial, pollNew, schedule]);

  // Persist draft in sessionStorage (debounced 400ms) → sopravvive a
  // refresh/reload e viene ripristinata al re-mount.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        if (draft) sessionStorage.setItem(DRAFT_KEY(conversationId), draft);
        else sessionStorage.removeItem(DRAFT_KEY(conversationId));
      } catch { /* silent */ }
    }, 400);
    return () => clearTimeout(t);
  }, [draft, conversationId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Auto-open modal se il pulsante "CRM" nella Inbox ha portato qui con ?add=1
  useEffect(() => {
    if (searchParams.get("add") === "1" && !convMeta.customer_id && !loading) {
      setAddModalOpen(true);
      searchParams.delete("add");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, convMeta.customer_id, loading]);

  const bubbles = useMemo(() =>
    messages.filter((m) => m.type !== "reaction"), [messages]);

  const isMatched = !!convMeta.customer_id;
  const headerName = convMeta.customer_name
    || convMeta.display_name
    || formatWaId(convMeta.wa_id_e164 || ("+" + (convMeta.wa_id || "")));

  // M2.5 P0-4C · Diagnostica Super Admin: apre AdminIntegrations con
  // conversation_id pre-selezionato. Nessuna nuova logica backend.
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const canSendTemplate = isSuper || (Array.isArray(user?.permissions)
    && user.permissions.includes("staff.messaging.templates.send"));
  const openDiagnostic = useCallback(() => {
    if (!conversationId) return;
    nav(`/admin/integrations?diag_conversation_id=${encodeURIComponent(conversationId)}#contact-diagnostic-panel`);
  }, [conversationId, nav]);

  const onCustomerLinked = ({ customer_id, customer_name }) => {
    setConvMeta((prev) => ({ ...prev, customer_id, customer_name }));
  };

  // M2.3 · Send message handler.
  // Offline UX (Fase B): quando `online=false` il pulsante è disabilitato e
  // un banner rosso avvisa che il messaggio NON è stato inviato. La draft
  // resta preservata in sessionStorage → al ritorno online l'operatore può
  // premere Invia normalmente. NO auto-send retry indiscriminato.
  const sendMessage = useCallback(async () => {
    const body = draft.trim();
    if (!body || sending) return;
    if (!online) {
      setSendError("Sarabi è offline · il messaggio NON è stato inviato. "
                   + "Il testo resta salvato: riprova quando torni online.");
      return;
    }
    // P0-5 · Guardia frontend: se il BE ha già segnalato window closed,
    // NON tentare l'invio. Il backend è comunque l'autorità.
    if (!windowState.open) {
      setSendError(null);
      setTemplatePickerOpen(true);
      return;
    }
    // Genera un nuovo client_request_id SOLO se non ne abbiamo uno in retry.
    if (!clientReqIdRef.current) {
      clientReqIdRef.current = (window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `sb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    }
    setSending(true);
    setSendError(null);
    try {
      const { data } = await api.post(
        `/staff/messaging/whatsapp/conversations/${conversationId}/messages`,
        {
          type: "text",
          text: body,
          client_request_id: clientReqIdRef.current,
        },
      );
      // Success → append messaggio canonico (dedup gestito da mergeMessages).
      if (data?.message) {
        mergeMessages([data.message]);
      }
      setDraft("");
      try { sessionStorage.removeItem(DRAFT_KEY(conversationId)); } catch {}
      clientReqIdRef.current = null;   // consuma il token idempotency
      // Reset height del textarea
      if (textareaRef.current) textareaRef.current.style.height = "auto";
      // Poll dopo brevi ms per catturare eventuali status delivered arrivati subito
      setTimeout(() => { pollNew(); }, 800);
    } catch (e) {
      // P0-5 · Fallback strutturato: 409 whatsapp_window_closed →
      // aggiorna stato locale, NON mostrare error generico, apri picker.
      const respData = e?.response?.data;
      if (e?.response?.status === 409
          && respData?.error === "whatsapp_window_closed") {
        setWindowState({
          open: false,
          expires_at: respData.window_expires_at || null,
          last_inbound_at: respData.last_customer_inbound_at || null,
        });
        setSendError(null);
        // preserva la draft, apri il picker template
        setTemplatePickerOpen(true);
        return;
      }
      const msg = formatApiError(e);
      setSendError(msg || "Impossibile inviare il messaggio. Riprova.");
      // NON reset clientReqIdRef → retry con stesso token è idempotente lato server.
    } finally {
      setSending(false);
    }
  }, [conversationId, draft, sending, mergeMessages, pollNew, online, windowState.open]);

  // P0-5 · Label "Puoi rispondere fino alle HH:mm" quando window è aperta.
  const windowExpiresLabel = useMemo(() => {
    if (!windowState.open || !windowState.expires_at) return null;
    try {
      const d = new Date(windowState.expires_at);
      return d.toLocaleTimeString("it-IT",
        { hour: "2-digit", minute: "2-digit" });
    } catch { return null; }
  }, [windowState.open, windowState.expires_at]);

  const onTemplateSent = useCallback((data) => {
    // Force refresh — il polling già lo fa ma vogliamo feedback immediato.
    setTimeout(() => { pollNew(); }, 400);
    // La draft può restare o essere rimossa: la preserviamo (l'operatore
    // potrebbe volere continuare il pensiero via template + poi commentare).
  }, [pollNew]);

  return (
    <div data-testid="staff-messages-thread"
         className="max-w-5xl mx-auto flex flex-col h-[calc(100vh-6rem)] font-sans">
      <div className="flex items-center gap-3 mb-3 flex-shrink-0">
        <button onClick={() => nav("/staff/messages")}
                data-testid="thread-back-btn"
                className="p-2 -ml-2 rounded-full hover:bg-sand-100">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-xl font-semibold truncate text-ink-900"
               data-testid="thread-header-name">
            {headerName}
          </div>
          <div className="text-xs text-ink-500">WhatsApp</div>
        </div>
        <AudioStatusBar />
        {isSuper && (
          <button onClick={openDiagnostic}
                  data-testid="thread-super-diag-btn"
                  title="Diagnostica questa conversazione (Super Admin, READ-ONLY)"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                              border-2 border-red-300 text-red-700 bg-red-50 text-sm font-semibold
                              hover:bg-red-100 transition">
            <Bug className="w-4 h-4" />
            <span className="hidden sm:inline">Diagnostica</span>
          </button>
        )}
        {!isMatched && !loading && (
          <button onClick={() => setAddModalOpen(true)}
                  data-testid="thread-add-crm-btn"
                  title={convMeta.customer_match_status === "ambiguous"
                    ? "Più clienti CRM con questo numero: verifica manuale" : undefined}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                              bg-bronze-600 text-white text-sm font-semibold
                              hover:bg-bronze-700 transition">
            <UserPlus className="w-4 h-4" />
            <span data-testid="thread-add-crm-label">
              {convMeta.customer_match_status === "ambiguous" ? "Verifica collegamento CRM" : "Aggiungi al CRM"}
            </span>
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto rounded-3xl border-2 border-sand-200 bg-[#ECE5DD]/60 py-3 space-y-1.5">
        {loading && messages.length === 0 && (
          <div data-testid="thread-loading" className="text-center text-ink-500 text-sm py-6">
            Caricamento…
          </div>
        )}
        {error && (
          <div data-testid="thread-error"
               className="mx-2 rounded-2xl border-2 border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {!loading && !error && bubbles.length === 0 && (
          <div data-testid="thread-empty" className="text-center text-ink-500 text-sm py-10">
            Nessun messaggio.
          </div>
        )}
        {(() => {
          // M2.5 P0-C · Interleave separator quando cambia il giorno
          // Europe/Rome. Nessun duplicato: chiave calcolata una volta per
          // messaggio, confrontata con prev. Funziona con polling incr.
          // + caricamento storico (ordine cronologico già gestito dal
          // merge upstream).
          const now = new Date();
          const out = [];
          let prevKey = null;
          for (const m of bubbles) {
            const k = messageDayKey(m);
            if (k && k !== prevKey) {
              out.push(
                <DaySeparator key={`sep-${k}`}
                                label={daySeparatorLabel(k, now)} />);
              prevKey = k;
            }
            out.push(<MessageBubble key={m.id} m={m}
                                       onOpenLightbox={setLightbox} />);
          }
          return out;
        })()}
        <div ref={endRef} />
      </div>

      <div className="flex-shrink-0 mt-3">
        {!online && (
          <div data-testid="composer-offline-note"
               className="mb-2 rounded-2xl border-2 border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 flex items-center gap-2">
            <WifiOff className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1 min-w-0">
              Offline · impossibile inviare ora. Il testo resta salvato: al ritorno online premi Invia.
            </span>
          </div>
        )}
        {/* P0-5 · Window CLOSED → blocco esplicito + CTA template */}
        {!windowState.open && (
          <div data-testid="window-closed-block"
               className="mb-2 rounded-2xl border-2 border-red-200 bg-red-50 p-3.5 space-y-2">
            <div className="flex items-start gap-2">
              <Clock className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div data-testid="window-closed-title"
                     className="text-sm font-semibold text-red-900">
                  Finestra WhatsApp scaduta
                </div>
                <div data-testid="window-closed-message"
                     className="text-xs text-red-800 mt-1">
                  Sono trascorse più di 24 ore dall'ultimo messaggio del cliente. Per ricontattarlo devi utilizzare un template WhatsApp approvato.
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              {canSendTemplate ? (
                <button
                  data-testid="window-closed-cta-template"
                  onClick={() => setTemplatePickerOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-bronze-600 text-white text-sm font-semibold hover:bg-bronze-700 transition"
                >
                  <FileText className="w-4 h-4" />
                  Invia template WhatsApp
                </button>
              ) : (
                <span data-testid="window-closed-no-permission"
                      className="text-xs text-ink-500 italic">
                  Contatta un manager per inviare un template
                </span>
              )}
            </div>
          </div>
        )}
        {/* P0-5 · Window OPEN → hint discreto con scadenza */}
        {windowState.open && windowExpiresLabel && (
          <div data-testid="window-open-hint"
               className="mb-2 text-[11px] text-ink-500 flex items-center gap-1.5 px-1">
            <Clock className="w-3 h-3" />
            <span>Puoi rispondere liberamente fino alle {windowExpiresLabel}</span>
          </div>
        )}
        {sendError && (
          <div data-testid="composer-error"
               className="mb-2 rounded-2xl border-2 border-red-200 bg-red-50 p-2.5 text-xs text-red-800 flex items-center justify-between gap-2">
            <span className="flex-1 min-w-0 break-words">{sendError}</span>
            <button onClick={() => setSendError(null)}
                    data-testid="composer-error-dismiss"
                    className="px-2 py-1 rounded-full bg-white border border-red-200 text-red-700 text-xs">
              Chiudi
            </button>
          </div>
        )}
        <div className="flex items-end gap-2 relative">
          <div className="relative">
            <button
              type="button"
              data-testid="composer-emoji-btn"
              onClick={() => setEmojiOpen((v) => !v)}
              disabled={sending || !windowState.open}
              className="flex-shrink-0 w-11 h-11 rounded-full border-2 border-sand-300
                          hover:bg-sand-100 disabled:opacity-50 inline-flex items-center
                          justify-center text-ink-700"
            >
              <Smile className="w-5 h-5" />
            </button>
            <EmojiPicker
              open={emojiOpen}
              onClose={() => setEmojiOpen(false)}
              onSelect={(emo) => {
                const el = textareaRef.current;
                if (!el) { setDraft((prev) => prev + emo); return; }
                const start = el.selectionStart ?? draft.length;
                const end = el.selectionEnd ?? draft.length;
                const next = draft.slice(0, start) + emo + draft.slice(end);
                setDraft(next);
                // Ripristina cursore dopo emoji + preserva focus
                requestAnimationFrame(() => {
                  if (!textareaRef.current) return;
                  textareaRef.current.focus();
                  const pos = start + emo.length;
                  try { textareaRef.current.setSelectionRange(pos, pos); }
                  catch { /* noop */ }
                });
              }}
            />
          </div>
          <textarea
            ref={textareaRef}
            data-testid="composer-input"
            rows={1}
            value={draft}
            disabled={sending || !windowState.open}
            onChange={(e) => {
              setDraft(e.target.value);
              // auto-resize (max 5 righe)
              const el = e.target;
              el.style.height = "auto";
              el.style.height = Math.min(el.scrollHeight, 120) + "px";
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder={windowState.open
              ? "Scrivi un messaggio…"
              : "Finestra 24h scaduta · usa un template"}
            className="flex-1 rounded-2xl border-2 border-sand-300 bg-white
                        px-4 py-2.5 text-[15px] leading-[1.35] text-ink-900
                        resize-none focus:outline-none focus:border-bronze-500
                        disabled:bg-sand-100 disabled:text-ink-400"
          />
          <button
            data-testid="composer-send-btn"
            onClick={sendMessage}
            disabled={sending || !draft.trim() || !online || !windowState.open}
            title={
              !online ? "Sarabi offline — impossibile inviare ora"
                : !windowState.open ? "Finestra 24h scaduta — usa un template"
                : undefined
            }
            className="flex-shrink-0 w-11 h-11 rounded-full bg-bronze-600 text-white
                        hover:bg-bronze-700 disabled:bg-sand-300 disabled:text-ink-400
                        disabled:cursor-not-allowed inline-flex items-center justify-center
                        transition">
            {sending
              ? <span data-testid="composer-sending"
                      className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {lightbox && (
        <div data-testid="lightbox"
             onClick={() => setLightbox(null)}
             className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4">
          <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
                  data-testid="lightbox-close"
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20">
            <X className="w-6 h-6 text-white" />
          </button>
          <img src={lightbox} alt="anteprima"
               className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}

      {addModalOpen && (
        <AddCustomerModal
          conversationId={conversationId}
          prefillPhone={convMeta.wa_id_e164 || ("+" + (convMeta.wa_id || ""))}
          matchStatus={convMeta.customer_match_status}
          onClose={() => setAddModalOpen(false)}
          onLinked={onCustomerLinked}
        />
      )}

      {templatePickerOpen && (
        <TemplatePickerModal
          conversationId={conversationId}
          defaultFirstName={
            (convMeta.customer_name || "").split(" ")[0] || ""
          }
          onClose={() => setTemplatePickerOpen(false)}
          onSent={onTemplateSent}
        />
      )}
    </div>
  );
}
